import { markForPack } from "./marks.js";
import { renderCardFace } from "./card-render.js";

const SUIT_COLOR_VAR = {
  action: "--c-suit-action",
  observation: "--c-suit-observation",
  relation: "--c-suit-relation",
};

function pathsToSvgInner(paths) {
  return paths
    .map((p) => {
      if (p.kind === "dot") {
        return `<path d="${p.d}" fill="currentColor" stroke="none"/>`;
      }
      if (p.kind === "dashed") {
        return `<path d="${p.d}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-dasharray="2 3"/>`;
      }
      return `<path d="${p.d}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`;
    })
    .join("");
}

function buildPackMarkSvg() {
  const pm = markForPack();
  const layers = pm.layers
    .map((layer) => {
      const colorVar = SUIT_COLOR_VAR[layer.suit];
      return `<g class="pack-layer pack-layer-${layer.suit}" style="color: var(${colorVar})">${pathsToSvgInner(layer.paths)}</g>`;
    })
    .join("");
  return `<svg viewBox="0 0 96 96" width="96" height="96" aria-hidden="true">${layers}</svg>`;
}

export function createPack({ rootEl, presence, onOpened }) {
  rootEl.innerHTML = `
    <div class="pack-stage">
      <div class="pack-status" id="pack-status">Connecting…</div>
      <button class="pack" id="pack" aria-label="Open a pack of cards" disabled>
        <div class="pack-mark" id="pack-mark"></div>
        <div class="pack-label">Pack</div>
      </button>
      <p class="pack-hint" id="pack-hint">Open one. Releases everyone else's pack. When someone else opens, yours comes back.</p>
      <div class="reveal" id="reveal"></div>
      <div class="reveal-continue" id="continue"><button class="reveal-btn" id="continue-btn">Continue</button></div>
    </div>
  `;

  rootEl.querySelector("#pack-mark").innerHTML = buildPackMarkSvg();

  const elPack = rootEl.querySelector("#pack");
  const elStatus = rootEl.querySelector("#pack-status");
  const elHint = rootEl.querySelector("#pack-hint");
  const elReveal = rootEl.querySelector("#reveal");
  const elContinue = rootEl.querySelector("#continue");
  const elContinueBtn = rootEl.querySelector("#continue-btn");

  let armed = false;
  let opening = false;

  function setArmed(next) {
    armed = next;
    elPack.disabled = !armed;
    elPack.classList.toggle("ready", armed);
    elPack.classList.remove("opened", "opening", "dispersing");
    elPack.style.display = "flex";
    elStatus.style.opacity = "1";
    if (armed) {
      elStatus.textContent = "Ready";
      elStatus.classList.add("ready");
      elHint.textContent = "Open one. Releases everyone else's pack.";
    } else {
      elStatus.textContent = "Waiting";
      elStatus.classList.remove("ready");
      elHint.textContent = "Your next pack comes when someone else opens.";
    }
  }
  setArmed(false);

  presence.on("welcome", (m) => setArmed(m.armed));
  presence.on("cascade", () => { if (!opening) setArmed(true); });

  function revealCards(cards) {
    opening = true;
    const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    elPack.classList.add("opening");
    elStatus.style.opacity = "0";

    setTimeout(() => {
      elPack.classList.remove("opening");
      elPack.classList.add("dispersing");
    }, reduced ? 0 : 90);

    setTimeout(() => {
      elPack.classList.add("opened");
      elPack.style.display = "none";

      cards.forEach((card, i) => {
        const el = renderCardFace(card);
        el.classList.add("revealing");
        elReveal.appendChild(el);
        requestAnimationFrame(() => el.classList.add("visible"));
        setTimeout(() => el.classList.add("mark-drawing"),
                   reduced ? 0 : 250 + i * 220);
        setTimeout(() => el.classList.add("text-in"),
                   reduced ? 100 : 250 + i * 220 + 720);
      });

      setTimeout(() => elContinue.classList.add("visible"),
                 reduced ? 200 : 250 + (cards.length - 1) * 220 + 720 + 420);
    }, reduced ? 50 : 550);
  }

  presence.on("opened", (m) => {
    revealCards(m.cards);
    onOpened?.(m.cards);
    setArmed(false);
  });

  elPack.addEventListener("click", () => {
    if (!armed || opening) return;
    presence.send({ type: "open" });
  });

  elContinueBtn.addEventListener("click", () => {
    elReveal.innerHTML = "";
    elContinue.classList.remove("visible");
    opening = false;
    elPack.style.display = "flex";
    elPack.classList.remove("opening", "dispersing", "opened");
    elStatus.style.opacity = "1";
    setArmed(armed);
  });
}
